// @flow
import { pender } from 'redux-pender';
import type { $AxiosError, $AxiosXHR } from 'axios';
import koLocale from 'date-fns/locale/ko';
import distanceInWords from 'date-fns/distance_in_words';
import dataFormat from 'date-fns/format';
import removeMd from 'remove-markdown';

export const pressedEnter = (fn: () => void) => (e: KeyboardEvent) => {
  if (e.key === 'Enter') {
    fn();
  }
  return null;
};

type Reducer = (state: any, action: any) => any;

export function applyPenders<T: Reducer>(reducer: T, penders: any[]): T {
  const updaters = Object.assign({}, ...penders.map(pender));
  return ((state, action) => {
    if (updaters[action.type]) {
      return updaters[action.type](state, action);
    }
    return reducer(state, action);
  }: any);
}

export type ResponseAction = {
  type: string,
  payload: $AxiosXHR<*>,
  error: $AxiosError<*>,
  meta: any,
};

export type GenericResponseAction<D, M = any> = {
  type: string,
  payload: {
    data: D,
  },
  meta: M,
};

type Return_<R, F: (...args: Array<any>) => R> = R;
export type Return<T> = Return_<*, T>;

export const getScrollTop = () => {
  if (!document.body) return 0;
  const scrollTop = document.documentElement
    ? document.documentElement.scrollTop || document.body.scrollTop
    : document.body.scrollTop;
  return scrollTop;
};

export const getScrollBottom = () => {
  if (!document.body) return 0;
  const { scrollHeight } = document.body;
  const { innerHeight } = window;
  const scrollTop = getScrollTop();
  return scrollHeight - innerHeight - scrollTop;
};

export const smoothScrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' })
};

export const preventStickBottom = () => {
  const scrollBottom = getScrollBottom();
  if (scrollBottom !== 0) return;
  if (document.documentElement) {
    document.documentElement.scrollTop -= 1;
  } else {
    if (!document.body) return;
    document.body.scrollTop -= 1;
  }
};

export const escapeForUrl = (text: string): string => {
  return text
    .replace(/\//g, '-')
    .replace(
      /[^0-9a-zA-Zㄱ-힣.\u3000-\u303f\u3040-\u309f\u30a0-\u30ff\uff00-\uff9f\u4e00-\u9faf\u3400-\u4dbf -]/g,
      '',
    )
    .replace(/ /g, '-')
    .replace(/--+/g, '-');
};

export const fromNow = (date: string) => {
  const now = new Date();
  const givenDate = new Date(date);
  const diff = now - givenDate;
  if (diff < 1000 * 60) {
    return '방금 전';
  }
  if (diff < 1000 * 60 * 60 * 24 * 7) {
    const distanceString = distanceInWords(now, givenDate, { locale: koLocale, addSuffix: true });
    return distanceString;
  }
  return dataFormat(givenDate, 'YYYY년 M월 D일');
};

export function convertToPlainText(markdown: string): string {
  const replaced = markdown
    .replace(/  +/g, '')
    .replace(/--/g, '')
    .replace(/\|/g, '')
    .replace(/\n/g, ' ')
    .replace(/```(.*)```/g, '')
    .replace(/[<>]/g, '');

  return (
    removeMd(replaced.slice(0, 500))
      .slice(0, 200)
      .replace(/#/g, '') + (replaced.length > 200 ? '...' : '')
  );
}

export function resizeImage(
    url: string,
    width: number = 250,
    height: number = 250,
    type: string = 'crop',
    quality: string = '82',
    format: string = 'webp',
  ) {
  if (!url || undefined) url = 'https://images.cacheby.com/no_image_2.png';

  if (url.indexOf('data:image/') !== -1) return url;
  const isEncoded = str =>
    typeof str === 'string' && decodeURI(str) !== str ? str : encodeURI(str);
  if (!url.startsWith('https://images.cacheby.com')) return isEncoded(url);
  return `${isEncoded(url)}?s=${width}x${height}&t=${type}&q=${quality}&f=${format}`;
}

export function loadScript(url: string) {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.onload = function onload() {
      resolve();
    };
    script.onerror = function onerror() {
      reject();
    };
    script.src = url;
    if (!document || !document.head) return;
    document.head.appendChild(script);
  });
}

export function imageURL(image, width, height) {
  if (!image) return '';
  return `${image}?s=${width}x${height}&t=crop&q=82&f=webp`;
}

export function parseArrayString(value) {
  return typeof value === 'string' ?
    value.split(',').filter(v => v) :
    value;
}

export const numberWithCommas = x => x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

export const orderShippingStatus = order => {
  if (!order) return null;

  if (order.status === 'cancelled') {
    return null;
  }

  // const hasTangibleItem = order.items
  //   .reduce((items, item) => {
  //     return items.concat(item, item.bundleItems || []);
  //   }, [])
  //   .some(item => item.length);
  //
  // if (!hasTangibleItem) return null;

  if (order.receivedAt) {
    return 'received';
  }

  const countByStatus = order.fulfillments.reduce((o, { status }) => {
    o[status] = o[status] || 0;
    o[status] += 1;
    return o;
  }, {});

  if (order.fulfillments.length === 0 || order.fulfillments.length === countByStatus.pending) {
    return 'pending';
  }

  if (order.fulfillments.length === countByStatus.arrived) {
    return 'arrived';
  }

  return 'shipped';
};

export const translationKeys = {
  'name.first': 'firstName',
  'name.last':  'lastName',
  'name.full':  'fullName',
};

export const statusTrans = key => {
  let words = {
    statusPlaced: '결제 대기',
    statusCancelledByStore: '운영자 취소',
    statusCancelledByCustomer: '고객 취소',
    statusPaid: '결제 완료',
    statusUnderPaid: '부분 결제',
    statusOverPaid: '초과 결제',
    statusRefunded: '환불 완료',
    statusPartiallyRefunded: '부분 환불 완료',
    statusUnderRefunded: '환불 처리 중',
    statusOverRefunded: '초과 환불',
    shippingStatusPending: '배송 대기',
    shippingStatusShipped: '배송중',
    shippingStatusArrived: '배송 완료',
    shippingStatusReceived: '수령 완료',
    refundStatusRequested: '승인 대기',
    refundStatusCancelledByStore: '운영자 취소',
    refundStatusCancelledByCustomer: '고객 취소',
    refundStatusAccepted: '환불 승인',
    orderTotalPaid: '결제 금액',
    orderTotalCancelled: '결제 취소 금액',
    orderTotalRefunded: '그 외 환불 금액',
    customerFirstName: '주문자 이름',
    customerLastName: '주문자 성',
    customerFullName: '주문자',
    customerEmail: '이메일',
    customerMobile: '연락처',
    customerPhone: '사업자등록번호',
    orderShippingAddress: '배송지 주소',
    addressFirstName: '주문자 이름',
    addressLastName: '주문자 성',
    addressFullName: '주문자',
    addressMobile: '연락처',
    addressPhone: '사업자등록번호',
    fulfillmentStatusPending: '배송 대기',
    fulfillmentStatusShipped: '배송중',
    fulfillmentStatusArrived: '배송 완료',
  };
  return words[key];
};


// WEBPACK FOOTER //
// ./src/lib/common.js